import type { WithActiveBranchId } from '@app/api/shared';
import type { ModelTag } from '@app/api/Tags';

export enum RobotsEndpoints {
  GetRobotsListTypes = 'GetRobotsListTypes',
  GetRobotsListTriggers = 'GetRobotsListTriggers',
  GetRobotsListForStageId = 'GetRobotsListForStageId',
  GetRobotForId = 'GetRobotForId',
  DeleteRobot = 'DeleteRobot',
  UpdateRobot = 'UpdateRobot',
  CreateRobot = 'CreateRobot',
  SearchRobotLogs = 'SearchRobotLogs',
  LogsMarkAsRead = 'LogsMarkAsRead',
  GetRobotLogsSearchResult = 'GetRobotLogsSearchResult',
  CheckKeywordOverlaps = 'CheckKeywordOverlaps',
}

export enum RobotType {
  Notification = 1,
  TemplateMessage = 2,
  CrmTask = 3,
  ChangeResponsible = 100,
}

export enum RobotEventTimeShift {
  Custom = 0,
  Time15Minutes = 1,
  Time30Minutes = 2,
  Time1Hour = 3,
  Time3Hours = 4,
  Time6Hours = 5,
  Time12Hours = 6,
  Time1Day = 7,
}

export interface ModelRobotList {
  Id: number;
  Trigger: string | null;
  Type: RobotType;
}

export interface ModelRobotBase {
  TriggerType: string;
  EventTimeShift: RobotEventTimeShift;
  CustomTimeShift: string | null;
  Comment: string | null;
  TriggerParameters: Record<string, unknown>;
  TypeParameters: Record<string, unknown>;
  AfterExecutionTags: number[];
  IgnoreSchedule?: boolean | null;
}

export interface ModelCreateRobot extends ModelRobotBase {
  SalesFunnelStageId: number;
  RobotType: RobotType;
  ApplyToExistingLeads?: boolean;
}

export interface ModelUpdateRobot extends ModelRobotBase {}

export interface ModelRobotGet {
  Id: number;
  SalesFunnelStageId: number;
  RobotType: RobotType;
  TriggerType: string;
  EventTimeShift: RobotEventTimeShift;
  CustomTimeShift: string | null;
  Comment: string | null;
  TriggerParameters: Record<string, unknown>;
  TypeParameters: Record<string, unknown>;
  AfterExecutionTags: ModelTag[];
  IgnoreSchedule?: boolean | null;
}

export interface ModelRobotsLogLead {
  Id: number;
  ClientId: number | null;
  ClientName: string | null;
}

export interface ModelRobotLogSearchFilter {
  RobotIds: number[] | null;
  SalesFunnelIds: (number | null)[] | null;
  LeadIds: (number | null)[] | null;
  SearchTerm: string | null;
  DateS: string | null;
  DateE: string | null;
  IsRead: boolean | null;
  ItemsPerPage: number;
}

export interface ModelRobotLog {
  Id: number;
  CreatedAt: string;
  Message: string | null;
  RobotId: number | null;
  IsRead: boolean;
  Lead?: ModelRobotsLogLead | null;
}

export interface ModelRobotLogsSearchResult {
  Id: number;
  Filter: ModelRobotLogSearchFilter;
  Items: ModelRobotLog[] | null;
  PageN: number;
  CountPages: number;
  CountItems: number;
}

export enum RobotLogMarkType {
  All = 0,
  Filtered = 1,
  Selected = 2,
}

export interface ModelRobotLogMarkAsRead {
  MarkType: RobotLogMarkType;
  Filter: ModelRobotLogSearchFilter;
  LogIds: (number | null)[] | null;
}

export interface ModelRobotLogMarkAsReadResult {
  LogIds: number[] | null;
}

// Проверка пересечений кодовых слов черновика робота «Сообщение по ключевым
// словам» (task/11645, beauty-crm task/11645 @0b0f55a2, договор техлида).
// TypeParameters — та же форма, что уходит в Create и Update.
export interface ModelCheckKeywordOverlaps {
  // null — робот ещё не сохранён
  RobotId: number | null;
  TriggerParameters?: Record<string, unknown>;
  TypeParameters: Record<string, unknown>;
}

// Имена — как в C# (KeywordOverlapType)
export enum KeywordOverlapType {
  // Слова перекрывают друг друга
  Mutual = 0,
  // Этот робот перекрывает другого
  RobotCoversOther = 1,
  // Другой робот перекрывает этот
  OtherCoversRobot = 2,
}

export interface ModelKeywordOverlap {
  OtherRobotId: number;
  OtherRobotName: string;
  OtherSalesFunnelId: number;
  OtherSalesFunnelName: string;
  OtherSalesFunnelStageId: number;
  OtherSalesFunnelStageName: string;
  OverlapType: KeywordOverlapType;
  Keyword: string | null;
  OtherKeyword: string | null;
  CoveringKeyword: string | null;
  CoveredKeyword: string | null;
  RobotHasNoKeywords: boolean;
  OtherRobotHasNoKeywords: boolean;
  // Готовый текст строки предупреждения
  Message: string;
}

export interface ModelKeywordOverlapsResult {
  RobotId: number | null;
  HasOverlaps: boolean;
  TotalCount: number;
  // Строк больше 100 — в Overlaps только первые 100
  IsTruncated: boolean;
  Warning: string | null;
  Overlaps: ModelKeywordOverlap[];
}

export type GetRobotsListTypesRequest = WithActiveBranchId;
export type GetRobotsListTriggersRequest = WithActiveBranchId<{
  robotType: RobotType;
}>;
export type GetRobotsListForStageIdRequest = WithActiveBranchId<{
  stageId: number;
}>;
export type GetRobotForIdRequest = WithActiveBranchId<{
  id: number;
}>;
export type DeleteRobotRequest = WithActiveBranchId<{
  id: number;
}>;
export type UpdateRobotRequest = WithActiveBranchId<{
  id: number;
  data: ModelUpdateRobot;
}>;
export type CreateRobotRequest = WithActiveBranchId<{
  data: ModelCreateRobot;
}>;
export type SearchRobotLogsRequest = WithActiveBranchId<{
  data: ModelRobotLogSearchFilter;
}>;
export type LogsMarkAsReadRequest = WithActiveBranchId<{
  data: ModelRobotLogMarkAsRead;
}>;
export type CheckKeywordOverlapsRequest = WithActiveBranchId<{
  data: ModelCheckKeywordOverlaps;
}>;
export type GetRobotLogsSearchResultRequest = WithActiveBranchId<{
  searchId: number;
  page: number;
  rowsPerPage: number;
}>;

export type GetRobotsListTypesResponse = Record<string, string>;
export type GetRobotsListTriggersResponse = Record<string, string>;
export type GetRobotsListForStageIdResponse = ModelRobotList[];
export type GetRobotForIdResponse = ModelRobotGet;
export type DeleteRobotResponse = void;
export type UpdateRobotResponse = void;
export type CreateRobotResponse = void;
export type SearchRobotLogsResponse = ModelRobotLogsSearchResult;
export type LogsMarkAsReadResponse = ModelRobotLogMarkAsReadResult;
export type GetRobotLogsSearchResultResponse = ModelRobotLogsSearchResult;
export type CheckKeywordOverlapsResponse = ModelKeywordOverlapsResult;
